import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { Reveal } from "../lib/reveal";
import { useLang } from "../i18n/LanguageContext";
import { Activity, Leaf, Wind, Droplets, MapPin, Maximize2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AnimatedNumber({ value, suffix = "", inView }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value, {
      duration: 2.0,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.floor(v).toLocaleString("en-IN")),
    });
    return controls.stop;
  }, [value, inView]);
  return (
    <span>
      {display}
      {suffix}
    </span>
  );
}

const items = [
  {
    key: "tonnes_treated",
    icon: Activity,
    en: { label: "Tonnes of waste treated", note: "Legacy + daily, across active sites" },
    hi: { label: "उपचारित अपशिष्ट (टन)", note: "पुराने + दैनिक, सक्रिय स्थलों पर" },
  },
  {
    key: "odour_free_days",
    icon: Wind,
    en: { label: "Odour-free site-days", note: "Cumulative across live deployments" },
    hi: { label: "गंध-मुक्त साइट-दिवस", note: "सक्रिय तैनातियों पर संचित" },
  },
  {
    key: "co2e_avoided_tonnes",
    icon: Leaf,
    en: { label: "tCO₂e avoided", note: "Methane diversion vs. open landfill" },
    hi: { label: "tCO₂e से बचाव", note: "खुले लैंडफिल की तुलना में मीथेन डायवर्जन" },
  },
  {
    key: "bio_culture_litres",
    icon: Droplets,
    en: { label: "Litres of OED applied", note: "On-site bio-culture production" },
    hi: { label: "OED (लीटर) प्रयुक्त", note: "साइट पर बायो-कल्चर उत्पादन" },
  },
  {
    key: "sites_active",
    icon: MapPin,
    en: { label: "Active deployments", note: "Municipal & industrial sites" },
    hi: { label: "सक्रिय तैनातियाँ", note: "नगरीय व औद्योगिक स्थल" },
  },
];

export const ImpactDashboard = () => {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  // Fetch base + periodically tick a tiny +1 for live feel
  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/impact/stats`)
      .then((res) => {
        if (!cancel) setStats(res.data);
      })
      .catch(() => !cancel && setError(true));
    return () => {
      cancel = true;
    };
  }, []);

  // Live micro-tick: increment tonnes_treated & co2e_avoided_tonnes slowly
  useEffect(() => {
    if (!stats) return;
    const iv = setInterval(() => {
      setStats((s) =>
        s
          ? {
              ...s,
              tonnes_treated: s.tonnes_treated + 1,
              co2e_avoided_tonnes: Math.round(s.co2e_avoided_tonnes + 0.18 * 1),
              bio_culture_litres: s.bio_culture_litres + 1,
            }
          : s
      );
    }, 8000); // one tonne every ~8 seconds — feels alive but not gimmicky
    return () => clearInterval(iv);
  }, [!!stats]);

  return (
    <section
      id="impact-dashboard"
      ref={ref}
      data-testid="impact-dashboard"
      className="relative py-24 md:py-32 bg-forest text-bone overflow-hidden"
    >
      {/* subtle grain / noise */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <Reveal className="flex items-center gap-3 mb-6">
              <span className="w-2 h-2 rounded-full bg-terracotta animate-pulse" />
              <span className="text-xs tracking-[0.22em] uppercase font-semibold text-bone/70">
                {t("Live Impact", "प्रत्यक्ष प्रभाव")}
              </span>
            </Reveal>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.0] text-bone">
                {t(
                  "The work is measurable. And it is happening now.",
                  "काम मापने योग्य है। और अभी हो रहा है।"
                )}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 self-end">
            <Reveal delay={0.1}>
              <p className="text-base md:text-lg leading-relaxed text-bone/75">
                {t(
                  "Cumulative outcomes across active OED deployments — updated live from the field. Base figures reflect confirmed case-study data; the tally ticks with each tonne we process together.",
                  "सक्रिय OED तैनातियों पर संचित परिणाम — मैदान से प्रत्यक्ष अपडेट। आधार आँकड़े पुष्ट केस स्टडी से हैं; प्रत्येक उपचारित टन के साथ गणना बढ़ती है।"
                )}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/impact/wall"
                data-testid="impact-wall-link"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-bone/25 hover:border-bone/70 px-5 py-3 text-xs tracking-[0.18em] uppercase text-bone/80 hover:text-bone transition-colors"
              >
                <Maximize2 size={14} />
                {t("Present on the wall", "बड़ी स्क्रीन पर प्रस्तुत करें")}
              </Link>
            </Reveal>
          </div>
        </div>

        {error ? (
          <div className="text-bone/60 py-10" data-testid="impact-error">
            {t("Live data temporarily unavailable.", "प्रत्यक्ष डेटा अस्थायी रूप से अनुपलब्ध।")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it, i) => {
              const Icon = it.icon;
              const value = stats ? stats[it.key] : 0;
              const copy = lang === "hi" ? it.hi : it.en;
              return (
                <motion.div
                  key={it.key}
                  initial={{ opacity: 0, y: 24 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
                  className="relative bg-bone/[0.04] border border-bone/10 rounded-3xl p-8 backdrop-blur-sm hover:bg-bone/[0.07] transition-colors duration-500"
                  data-testid={`impact-card-${it.key}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-11 h-11 rounded-full bg-moss/20 border border-moss/40 flex items-center justify-center text-moss">
                      <Icon size={18} strokeWidth={1.6} />
                    </div>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-bone/40">
                      {it.key === "sites_active" ? t("Active", "सक्रिय") : t("Live", "लाइव")}
                    </span>
                  </div>
                  <div
                    className="font-serif text-5xl md:text-6xl leading-none text-bone tabular-nums"
                    data-testid={`impact-value-${it.key}`}
                  >
                    <AnimatedNumber value={value} inView={inView && !!stats} />
                  </div>
                  <div className="text-sm md:text-base text-bone mt-5">{copy.label}</div>
                  <div className="text-xs text-bone/50 mt-1">{copy.note}</div>
                </motion.div>
              );
            })}
          </div>
        )}

        <Reveal delay={0.2}>
          <div className="mt-10 text-xs tracking-[0.18em] uppercase text-bone/40" data-testid="impact-updated">
            {stats
              ? `${t("Last updated", "अंतिम अपडेट")} · ${new Date(stats.as_of).toLocaleString(lang === "hi" ? "hi-IN" : "en-IN")}`
              : t("Loading…", "लोड हो रहा है…")}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
